import { Carousel as NukaCarousel, SlideHandle } from 'nuka-carousel';
import { useCallback, useRef, useState } from 'react';

import Image from '@/components/image/NextImage';
import ChevronLeft from 'icons/chevron-left-aligned.svg';
import ChevronRight from 'icons/chevron-right-aligned.svg';

import {
	Arrow,
	AuthorContainer,
	AuthorHandle,
	AuthorImage,
	AuthorName,
	CarouselWrapper,
	Nav,
	NavDot,
	Slide,
	SlideCopy,
	StoryImage,
	Wrapper,
} from './styles';
import { CarouselProps } from './types';

const Carousel = ({ slides }: CarouselProps) => {
	const [currentSlide, setCurrentSlide] = useState(0);

	const carouselRef = useRef<SlideHandle>(null);

	const onNext = useCallback(() => {
		setCurrentSlide(_slide => (_slide + 1) % slides.length);
		carouselRef.current?.goForward();
	}, [slides.length]);

	const onPrev = useCallback(() => {
		setCurrentSlide(_slide => (_slide === 0 ? slides.length - 1 : _slide - 1));
		carouselRef.current?.goBack();
	}, [slides.length]);

	const onDrag = useCallback(
		(slideIndex: number) => {
			// As this callback happens after an animation, we need to make sure that we ignore "old" updates
			setCurrentSlide(_slide => (_slide !== currentSlide ? _slide : slideIndex));
		},
		[currentSlide],
	);

	const goToSlide = useCallback((slideIndex: number) => {
		setCurrentSlide(slideIndex);
		carouselRef.current?.goToPage(slideIndex);
	}, []);

	return (
		<Wrapper>
			<CarouselWrapper>
				<NukaCarousel ref={carouselRef} afterSlide={onDrag}>
					{slides.map(slide => (
						<Slide key={`slide-${slide.authorHandle}`}>
							<div>
								<AuthorContainer>
									<AuthorImage imageUrl={slide.authorImage} />
									<div>
										<AuthorName>{slide.authorName}</AuthorName>
										<AuthorHandle>{slide.authorHandle}</AuthorHandle>
									</div>
								</AuthorContainer>
								<SlideCopy>{slide.copy}</SlideCopy>
							</div>
							{slide.storyImage && (
								<StoryImage>
									<Image
										src={slide.storyImage}
										alt="Success Story Image"
										width={360}
										height={360}
										style={{ objectFit: 'cover' }}
									/>
								</StoryImage>
							)}
						</Slide>
					))}
				</NukaCarousel>
			</CarouselWrapper>
			<Nav>
				<Arrow as={ChevronLeft} onClick={onPrev} />
				{slides.map((slide, index) => (
					<NavDot
						isActive={currentSlide === index}
						key={`navDot-${slide.authorHandle}`}
						onClick={(): void => goToSlide(index)}
					/>
				))}
				<Arrow as={ChevronRight} onClick={onNext} />
			</Nav>
		</Wrapper>
	);
};

export default Carousel;
